import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Select, Radio, Space, Card, Switch, InputNumber, Button, Modal, Drawer } from 'antd';
import { FullscreenOutlined, SettingOutlined } from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  Label,
  LabelList,
} from 'recharts';
import { useTheme } from '../../contexts/useTheme';
import { useChartAnimations } from '../../contexts/useChartAnimations';
import { useObservationFields } from '../../hooks/useObservationFields';
import type { StationData } from '../../api/stationObservationsService';
import {
  getBucketStarts,
  getEffectiveBucketMs,
  type DownsamplingScale,
} from '../../utils/observationDownsampling';

export interface ChartSettings {
  selectedParameters: string[];
  chartType: 'line' | 'bar';
  showAverage: boolean;
  selectedStations: string[];
  useDualYAxis: boolean;
  useCombinationColors: boolean;
  showDataLabels: boolean;
  showLegend: boolean;
  yAxisSide: 'left' | 'right';
  yAxisScale: 'auto' | 'custom';
  yAxisStartValue: number;
  yAxisEndValue: number | null;
  downsamplingScale: DownsamplingScale;
  windArrowConvention: 'blowing-to' | 'coming-from';
}

interface AllStationsComparisonProps {
  stations: StationData[];
  circuitTimeZone?: string;
  sessions?: Array<{ start_time: string; end_time: string; session_name: string }>;
  /** When 'compact', hides controls and shows a settings button that opens a drawer */
  variant?: 'full' | 'compact';
  /** When true (default in compact), hide the chart card title */
  hideTitle?: boolean;
  /** Ref for compact mode: parent can render settings/fullscreen buttons and call these handlers */
  compactActionsRef?: React.RefObject<{
    openSettings: () => void;
    openFullScreen: () => void;
  } | null>;
  /** Date range for the chart (used to determine if downsampling applies). If omitted, derived from data. */
  dateRange?: { start: Date; end: Date };
  /** Hide the fullscreen button (e.g. when embedded in a dashboard widget) */
  hideFullScreen?: boolean;
  /** Controlled settings: when provided, used for initialization instead of localStorage */
  settings?: Partial<ChartSettings>;
  /** Called when any display setting changes (for parent-managed persistence) */
  onSettingsChange?: (settings: ChartSettings) => void;
  /** Force-disable chart animations regardless of global setting. Useful in resizable containers. */
  disableAnimations?: boolean;
  /** When true, strips inner card chrome for dashboard widget embedding */
  embeddedInWidget?: boolean;
}

type ChartType = 'line' | 'bar';

interface ChartDataPoint {
  timestamp: string;
  timeMs: number; // Numeric timestamp for reliable X scale and ReferenceArea with many points
  time: string;
  date: string;
  dateTimeLabel: string; // Combined label for x-axis display (date above, time below)
  [key: string]: string | number | null | undefined; // Dynamic station values, wind arrow Y positions, etc.
}

// Wind speed color scale (matches ForecastChartView and StationCharts)
const windSpeedColors = [
  { max: 5, color: '#00CED1', label: '< 5 km/h' },      // Cyan
  { max: 10, color: '#32CD32', label: '5-10 km/h' },    // Green
  { max: 15, color: '#FFD700', label: '10-15 km/h' },   // Yellow
  { max: 20, color: '#FFA500', label: '15-20 km/h' },   // Orange
  { max: Infinity, color: '#FF4444', label: '> 20 km/h' }, // Red
];

const getWindSpeedColor = (speed: number | null | undefined): string => {
  if (speed === null || speed === undefined) return '#666';
  for (const { max, color } of windSpeedColors) {
    if (speed < max) return color;
  }
  return '#FF4444';
};

/** Wind speed threshold (m/s) below which wind direction is treated as null (calm = unreliable direction) */
const CALM_WIND_THRESHOLD_MS = 0.5;

// Custom tick component for multi-line x-axis labels (date above, time below).
// Supports payload.value as string (date\ntime) or number (ms) when timeZone is provided.
const CustomXAxisTick = ({
  x,
  y,
  payload,
  textColor,
  timeZone,
  fontSize = 11,
}: {
  x: string | number;
  y: string | number;
  payload: { value: string | number };
  textColor: string;
  timeZone?: string;
  fontSize?: number;
}) => {
  let date: string;
  let time: string;
  if (typeof payload.value === 'number' && timeZone) {
    const d = new Date(payload.value);
    date = d.toLocaleDateString('en-US', { timeZone, month: 'short', day: '2-digit' });
    time = d.toLocaleTimeString('en-US', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false });
  } else {
    const parts = String(payload.value).split('\n');
    date = parts[0] ?? '';
    time = parts[1] ?? '';
  }
  const tickX = Number(x);
  const tickY = Number(y);
  const lineHeight = Math.round(fontSize * 1.25);
  return (
    <g transform={`translate(${tickX},${tickY + 8})`}>
      <text x={0} y={0} textAnchor="middle" fill={textColor} fontSize={fontSize}>
        <tspan x={0} dy={0}>{date}</tspan>
        <tspan x={0} dy={lineHeight}>{time}</tspan>
      </text>
    </g>
  );
};

// Custom legend component for both modes
interface CustomLegendProps {
  parameters: string[];
  stations: string[];
  getField: (param: string) => { display_name?: string } | null;
  textColor: string;
  useCombinationColors: boolean;
  fontSize?: number;
  /** When true (map panel), hide "Parameters:" and "Stations:" section labels */
  hideSectionLabels?: boolean;
  /** Tighter spacing for compact / widget layouts */
  compact?: boolean;
}

const CustomLegend: React.FC<CustomLegendProps> = ({ parameters, stations, getField, textColor, useCombinationColors, fontSize = 12, hideSectionLabels = false, compact = false }) => {
  const itemGap = compact ? 10 : 16;
  const sectionGap = compact ? 12 : 20;
  // Average mode: Just show parameters
  if (stations.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '16px', 
        flexWrap: 'wrap',
      }}>
        {parameters.map((param, index) => {
          const field = getField(param);
          const displayName = field?.display_name || param;
          const color = PARAMETER_COLORS[index % PARAMETER_COLORS.length];
          return (
            <div key={param} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '20px',
                height: '3px',
                backgroundColor: color,
                borderRadius: '2px',
              }} />
              <span style={{ color: textColor, fontSize }}>{displayName} - Average</span>
            </div>
          );
        })}
      </div>
    );
  }

  if (useCombinationColors) {
    // Colors mode: Show all combinations with unique colors
    let combinationColorIndex = 0;
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '12px', 
        flexWrap: 'wrap',
      }}>
        {parameters.map((param) => {
          const field = getField(param);
          const displayName = field?.display_name || param;
          return stations.map((station) => {
            const color = COMBINATION_COLORS[combinationColorIndex % COMBINATION_COLORS.length];
            combinationColorIndex++;
            return (
              <div key={`${param}-${station}`} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '20px',
                  height: '3px',
                  backgroundColor: color,
                  borderRadius: '2px',
                }} />
                <span style={{ color: textColor, fontSize }}>{displayName} - Station {station}</span>
              </div>
            );
          });
        })}
      </div>
    );
  }

  // Dashed mode: Show parameter colors and station dash patterns separately
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      gap: sectionGap, 
      flexWrap: 'wrap',
    }}>
      {/* Parameter Color Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: itemGap, flexWrap: 'wrap' }}>
        {!hideSectionLabels && <span style={{ color: textColor, fontWeight: 500, fontSize, marginRight: '8px' }}>Parameters:</span>}
        {parameters.map((param, index) => {
          const field = getField(param);
          const displayName = field?.display_name || param;
          const color = PARAMETER_COLORS[index % PARAMETER_COLORS.length];
          return (
            <div key={param} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '20px',
                height: '3px',
                backgroundColor: color,
                borderRadius: '2px',
              }} />
              <span style={{ color: textColor, fontSize }}>{displayName}</span>
            </div>
          );
        })}
      </div>

      {/* Station Dash Pattern Legend */}
      <div style={{ display: 'flex', alignItems: 'center', gap: itemGap, flexWrap: 'wrap' }}>
        {!hideSectionLabels && <span style={{ color: textColor, fontWeight: 500, fontSize, marginRight: '8px' }}>Stations:</span>}
        {stations.map((station, index) => {
          let strokeDasharray: string | undefined;
          let opacity = 1;
          
          if (index === 0) {
            strokeDasharray = undefined;
            opacity = 1;
          } else if (index === 1) {
            strokeDasharray = '5 5';
            opacity = 0.9;
          } else if (index === 2) {
            strokeDasharray = '10 5';
            opacity = 0.8;
          } else {
            strokeDasharray = '2 2';
            opacity = 0.7;
          }

          return (
            <div key={station} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="20" height="3" style={{ opacity }}>
                <line
                  x1="0"
                  y1="1.5"
                  x2="20"
                  y2="1.5"
                  stroke={textColor}
                  strokeWidth="2"
                  strokeDasharray={strokeDasharray}
                />
              </svg>
              <span style={{ color: textColor, fontSize }}>Station {station}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

type WindArrowConvention = 'blowing-to' | 'coming-from';

const WIND_ARROW_CONVENTION_KEY = 'allStationsWindArrowConvention';

// Custom arrow shape for wind direction markers
interface WindArrowProps {
  cx?: number;
  cy?: number;
  payload?: ChartDataPoint;
  fill?: string;
  stationRef?: string;
  convention?: WindArrowConvention;
}

const WindArrow: React.FC<WindArrowProps> = ({ cx, cy, payload, stationRef, convention = 'blowing-to' }) => {
  if (!cx || !cy || !payload) return null;

  const dirKey = stationRef
    ? `station_${stationRef}_windDirection`
    : 'average_windDirection';
  const speedKey = stationRef
    ? `station_${stationRef}_windSpeed`
    : 'average_windSpeed';

  const windDirection = typeof payload[dirKey] === 'number' ? (payload[dirKey] as number) : null;
  const windSpeed = typeof payload[speedKey] === 'number' ? (payload[speedKey] as number) : null;

  if (windDirection === null) return null;

  // Arrow shape points up (North). SVG rotate() is clockwise, matching compass bearings.
  // 'blowing-to': +180° shows where the wind is going.
  // 'coming-from': no offset, arrow points towards the source direction (meteorological convention).
  const rotation = convention === 'blowing-to'
    ? (windDirection + 180) % 360
    : windDirection;

  const arrowColor = getWindSpeedColor(windSpeed);

  return (
    <g transform={`translate(${cx}, ${cy}) rotate(${rotation})`}>
      <path
        d="M0,-9 L4.5,6 L0,3 L-4.5,6 Z"
        fill={arrowColor}
        stroke="#333"
        strokeWidth={0.5}
      />
    </g>
  );
};

// Color palette for parameters (each parameter gets its own color)
const PARAMETER_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
];

// Extended color palette for station+parameter combinations
const COMBINATION_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
  '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
  '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7',
  '#a29bfe', '#fd79a8', '#fdcb6e', '#e17055', '#00b894',
  '#00cec9', '#74b9ff', '#0984e3', '#6c5ce7', '#a29bfe',
];

const DOWNSAMPLING_SCALE_KEY = 'allStationsDownsamplingScale';

function loadDownsamplingScale(): DownsamplingScale {
  try {
    const stored = localStorage.getItem(DOWNSAMPLING_SCALE_KEY);
    if (stored === 'auto' || stored === '1' || stored === '10' || stored === '30' || stored === '60') {
      return stored;
    }
  } catch { /* ignore */ }
  return 'auto';
}

const DOWNSAMPLING_SCALE_OPTIONS: { value: DownsamplingScale; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: '1', label: '1 minute' },
  { value: '10', label: '10 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
];

export const AllStationsComparison: React.FC<AllStationsComparisonProps> = ({
  stations,
  circuitTimeZone,
  sessions = [],
  variant = 'full',
  hideTitle = variant === 'compact',
  compactActionsRef,
  dateRange,
  hideFullScreen = false,
  settings: controlledSettings,
  onSettingsChange,
  disableAnimations = false,
  embeddedInWidget = false,
}) => {
  const { theme } = useTheme();
  const { chartAnimationsEnabled: globalAnimations } = useChartAnimations();
  const chartAnimationsEnabled = disableAnimations ? false : globalAnimations;
  const { fields, getField, convertForDisplay, getDisplayUnit } = useObservationFields();
  const isDark = theme === 'dark';
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(variant === 'compact' ? 0 : 500);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [fullscreenChartHeight, setFullscreenChartHeight] = useState(500);
  const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);

  // Expose actions to parent when compactActionsRef is provided (map panel puts buttons in title)
  useEffect(() => {
    if (variant === 'compact' && compactActionsRef) {
      compactActionsRef.current = {
        openSettings: () => setIsSettingsDrawerOpen(true),
        openFullScreen: () => setIsFullScreen(true),
      };
      return () => {
        compactActionsRef.current = null;
      };
    }
  }, [variant, compactActionsRef]);

  // Controlled mode: when `controlledSettings` is provided, use those for initialization
  // and skip localStorage persistence. Otherwise, use localStorage (uncontrolled mode).
  const isControlled = !!onSettingsChange;

  // localStorage key for persisting chart preferences (uncontrolled mode only)
  const fieldsStorageKey = 'allStationsChartFields';
  const chartTypeStorageKey = 'allStationsChartType';
  
  // Load saved preferences from localStorage (uncontrolled mode)
  const loadSavedPreferences = () => {
    if (controlledSettings) {
      return {
        fields: controlledSettings.selectedParameters ?? ['temperature'],
        chartType: (controlledSettings.chartType as ChartType) ?? 'line',
      };
    }
    try {
      const savedFields = localStorage.getItem(fieldsStorageKey);
      const savedChartType = localStorage.getItem(chartTypeStorageKey);
      return {
        fields: savedFields ? JSON.parse(savedFields) : ['temperature'],
        chartType: (savedChartType as ChartType) || 'line',
      };
    } catch (error) {
      console.error('Error loading chart preferences from localStorage:', error);
      return { fields: ['temperature'], chartType: 'line' as ChartType };
    }
  };
  
  const savedPreferences = loadSavedPreferences();
  const [selectedParameters, setSelectedParameters] = useState<string[]>(savedPreferences.fields);
  const [chartType, setChartType] = useState<ChartType>(savedPreferences.chartType);
  
  // State for station selection and average toggle
  const [showAverage, setShowAverage] = useState<boolean>(controlledSettings?.showAverage ?? false);
  const [selectedStations, setSelectedStations] = useState<string[]>(() =>
    controlledSettings?.selectedStations ?? stations.map(s => s.station_reference)
  );
  const [useDualYAxis, setUseDualYAxis] = useState<boolean>(controlledSettings?.useDualYAxis ?? false);
  const [useCombinationColors, setUseCombinationColors] = useState<boolean>(controlledSettings?.useCombinationColors ?? false);
  const [showDataLabels, setShowDataLabels] = useState<boolean>(controlledSettings?.showDataLabels ?? false);
  const [showLegend, setShowLegend] = useState<boolean>(controlledSettings?.showLegend ?? true);
  const [yAxisSide, setYAxisSide] = useState<'left' | 'right'>(controlledSettings?.yAxisSide ?? 'left');
  const [yAxisScale, setYAxisScale] = useState<'auto' | 'custom'>(controlledSettings?.yAxisScale ?? 'auto');
  const [yAxisStartValue, setYAxisStartValue] = useState<number>(controlledSettings?.yAxisStartValue ?? 0);
  const [yAxisEndValue, setYAxisEndValue] = useState<number | null>(controlledSettings?.yAxisEndValue ?? null);

  const [downsamplingScale, setDownsamplingScale] = useState<DownsamplingScale>(() => {
    if (controlledSettings?.downsamplingScale) return controlledSettings.downsamplingScale;
    return loadDownsamplingScale();
  });
  const [windArrowConvention, setWindArrowConvention] = useState<WindArrowConvention>(() => {
    if (controlledSettings?.windArrowConvention) return controlledSettings.windArrowConvention;
    try {
      const stored = localStorage.getItem(WIND_ARROW_CONVENTION_KEY);
      if (stored === 'blowing-to' || stored === 'coming-from') return stored;
    } catch { /* ignore */ }
    return 'blowing-to';
  });

  // Report settings changes to parent (debounced to avoid excessive updates)
  const settingsChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!onSettingsChange) return;

    if (settingsChangeTimerRef.current) clearTimeout(settingsChangeTimerRef.current);
    settingsChangeTimerRef.current = setTimeout(() => {
      onSettingsChange({
        selectedParameters,
        chartType,
        showAverage,
        selectedStations,
        useDualYAxis,
        useCombinationColors,
        showDataLabels,
        showLegend,
        yAxisSide,
        yAxisScale,
        yAxisStartValue,
        yAxisEndValue,
        downsamplingScale,
        windArrowConvention,
      });
    }, 300);

    return () => {
      if (settingsChangeTimerRef.current) clearTimeout(settingsChangeTimerRef.current);
    };
  }, [onSettingsChange, selectedParameters, chartType, showAverage, selectedStations, useDualYAxis, useCombinationColors, showDataLabels, showLegend, yAxisSide, yAxisScale, yAxisStartValue, yAxisEndValue, downsamplingScale, windArrowConvention]);

  // Update selectedStations only when the actual set of stations changes (e.g., new event selected),
  // NOT on every data refresh in live mode (which creates a new array reference with the same stations).
  const stationRefsKey = useMemo(() => 
    stations.map(s => s.station_reference).sort().join(','), 
    [stations]
  );
  
  useEffect(() => {
    const allStationRefs = stations.map(s => s.station_reference);
    setSelectedStations(prev => {
      // If the set of available stations changed, reset to all stations
      const prevSorted = [...prev].sort().join(',');
      const newSorted = [...allStationRefs].sort().join(',');
      if (prevSorted !== newSorted) {
        // Check if all previous selections still exist in the new set
        const validPrev = prev.filter(ref => allStationRefs.includes(ref));
        if (validPrev.length === 0) {
          // No previous selections are valid, select all
          return allStationRefs;
        }
        // Keep only the valid previous selections
        return validPrev;
      }
      // Same stations, keep current selection
      return prev;
    });
  }, [stationRefsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Save preferences to localStorage when they change (uncontrolled mode only)
  useEffect(() => {
    if (isControlled) return;
    try {
      localStorage.setItem(fieldsStorageKey, JSON.stringify(selectedParameters));
    } catch (error) {
      console.error('Error saving chart fields to localStorage:', error);
    }
  }, [selectedParameters, isControlled]);
  
  useEffect(() => {
    if (isControlled) return;
    try {
      localStorage.setItem(chartTypeStorageKey, chartType);
    } catch (error) {
      console.error('Error saving chart type to localStorage:', error);
    }
  }, [chartType, isControlled]);

  useEffect(() => {
    if (isControlled) return;
    try {
      localStorage.setItem(DOWNSAMPLING_SCALE_KEY, downsamplingScale);
    } catch (error) {
      console.error('Error saving downsampling scale to localStorage:', error);
    }
  }, [downsamplingScale, isControlled]);

  useEffect(() => {
    if (isControlled) return;
    try {
      localStorage.setItem(WIND_ARROW_CONVENTION_KEY, windArrowConvention);
    } catch { /* ignore */ }
  }, [windArrowConvention, isControlled]);

  // Calculate chart height based on container (legend overlays bottom 60px via Recharts margin)
  useEffect(() => {
    const updateHeight = () => {
      if (chartContainerRef.current) {
        const rect = chartContainerRef.current.getBoundingClientRect();
        const calculatedHeight = Math.max(variant === 'compact' ? 180 : 300, rect.height);
        setChartHeight(calculatedHeight);
      } else {
        setChartHeight(variant === 'compact' ? 300 : 400);
      }
    };

    const timeoutId = setTimeout(updateHeight, 0);
    window.addEventListener('resize', updateHeight);

    // In compact (map panel) mode, observe container resize (e.g. panel resized by user)
    let resizeObserver: ResizeObserver | null = null;
    const container = chartContainerRef.current;
    if (variant === 'compact' && container) {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(container);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateHeight);
      resizeObserver?.disconnect();
    };
  }, [stations, variant]); // Re-run when stations or variant change

  // Update fullscreen chart height on resize when modal is open (debounced to avoid heavy re-renders)
  useEffect(() => {
    if (!isFullScreen) return;

    const updateFullscreenHeight = () => {
      const availableHeight = Math.max(400, window.innerHeight - 332);
      setFullscreenChartHeight(availableHeight);
    };

    updateFullscreenHeight(); // Initial size when modal opens
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedUpdate = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        updateFullscreenHeight();
      }, 200);
    };

    window.addEventListener('resize', debouncedUpdate);
    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [isFullScreen]);

  // Derive available parameters dynamically from station data + observation field metadata
  const availableParameters = useMemo(() => {
    // Collect all parameter names reported by any station
    const stationParamSet = new Set<string>();
    stations.forEach(station => {
      station.reading_config.forEach(config => stationParamSet.add(config.name));
    });
    const stationParams = Array.from(stationParamSet);

    // If observation fields are loaded, filter out hidden ones and sort by position
    if (fields.length > 0) {
      return stationParams
        .filter(name => {
          const field = getField(name);
          // If field exists in observation_fields, respect hidden flag; otherwise show it
          return !field || !field.hidden;
        })
        .sort((a, b) => {
          const fieldA = getField(a);
          const fieldB = getField(b);
          return (fieldA?.position ?? 999) - (fieldB?.position ?? 999);
        });
    }

    return stationParams;
  }, [stations, fields, getField]);

  // Validate selected parameters when available parameters change
  useEffect(() => {
    if (availableParameters.length === 0) return;
    setSelectedParameters(prev => {
      const valid = prev.filter(p => availableParameters.includes(p));
      if (valid.length === 0) {
        return [availableParameters[0]];
      }
      return valid.length !== prev.length ? valid : prev;
    });
  }, [availableParameters]);

  // Get field options for select with display names and units from observation fields
  const fieldOptions = useMemo(() => {
    return availableParameters.map(paramName => {
      const field = getField(paramName);
      const displayName = field?.display_name ||
        paramName.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
      const displayUnit = getDisplayUnit(paramName) || '';

      return {
        value: paramName,
        label: `${displayName}${displayUnit ? ` (${displayUnit})` : ''}`,
      };
    });
  }, [availableParameters, getField, getDisplayUnit]);

  // Filter stations based on selection
  const filteredStations = useMemo(() => {
    return stations.filter(station => selectedStations.includes(station.station_reference));
  }, [stations, selectedStations]);

  // Whether to show the downsampling scale select (when we have data)
  const showDownsamplingSelect = filteredStations.length > 0;

  // Transform all stations data into chart-ready format
  const chartData = useMemo<ChartDataPoint[]>(() => {
    if (filteredStations.length === 0 || selectedParameters.length === 0) return [];

    // Collect all unique timestamps from filtered stations
    const timestampSet = new Set<string>();
    filteredStations.forEach(station => {
      station.observations.forEach(obs => {
        timestampSet.add(obs.reading_timestamp);
      });
    });

    const allTimestamps = Array.from(timestampSet).sort();
    const minMs = allTimestamps.length > 0 ? new Date(allTimestamps[0]).getTime() : 0;
    const maxMs = allTimestamps.length > 0 ? new Date(allTimestamps[allTimestamps.length - 1]).getTime() : 0;
    const rangeDurationMs = dateRange
      ? dateRange.end.getTime() - dateRange.start.getTime()
      : maxMs - minMs;
    const bucketMs = getEffectiveBucketMs(downsamplingScale, rangeDurationMs);
    const applyDownsampling = bucketMs > 60 * 1000; // Downsample when bucket > 1 min

    /** Get observation for a station at a given time point. For downsampling, uses first obs in bucket. */
    const getObservation = (station: StationData, timestampOrBucketStart: string | number) => {
      if (typeof timestampOrBucketStart === 'string') {
        return station.observations.find(obs => obs.reading_timestamp === timestampOrBucketStart);
      }
      const bucketStartMs = timestampOrBucketStart;
      const bucketEndMs = bucketStartMs + bucketMs;
      return station.observations.find(obs => {
        const ms = new Date(obs.reading_timestamp).getTime();
        return ms >= bucketStartMs && ms < bucketEndMs;
      });
    };

    const timestampsOrBuckets: Array<{ timestamp: string; timeMs: number }> = applyDownsampling
      ? getBucketStarts(minMs, maxMs, bucketMs).map(bucketStartMs => ({
          timestamp: new Date(bucketStartMs).toISOString(),
          timeMs: bucketStartMs,
        }))
      : allTimestamps.map(ts => ({ timestamp: ts, timeMs: new Date(ts).getTime() }));

    const dataPoints = timestampsOrBuckets.map(({ timestamp, timeMs }) => {
      const datetime = new Date(timestamp);
      const timeStr = datetime.toLocaleTimeString('en-US', {
        timeZone: circuitTimeZone || undefined,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const dateStr = datetime.toLocaleDateString('en-US', {
        timeZone: circuitTimeZone || undefined,
        month: 'short',
        day: '2-digit',
      });
      const dataPoint: ChartDataPoint = {
        timestamp,
        timeMs,
        time: timeStr,
        date: dateStr,
        dateTimeLabel: `${dateStr}\n${timeStr}`, // Combined label with newline for multi-line display
      };

      const obsKey = applyDownsampling ? timeMs : timestamp;

      if (showAverage) {
        // Calculate average values across all filtered stations
        selectedParameters.forEach(param => {
          const values: number[] = [];

          if (param === 'windDirection') {
            // For wind direction: only include when wind speed >= threshold (calm = unreliable)
            filteredStations.forEach((station) => {
              const observation = getObservation(station, obsKey);
              if (observation) {
                const windSpeedIndex = station.reading_config.findIndex(config => config.name === 'windSpeed');
                const rawWindSpeed = windSpeedIndex >= 0 ? observation.readings[windSpeedIndex] : null;
                if (rawWindSpeed !== null && rawWindSpeed !== undefined && rawWindSpeed >= CALM_WIND_THRESHOLD_MS) {
                  const paramIndex = station.reading_config.findIndex(config => config.name === param);
                  if (paramIndex !== -1) {
                    const rawValue = observation.readings[paramIndex];
                    if (rawValue !== null && rawValue !== undefined) {
                      const displayValue = convertForDisplay(param, rawValue);
                      if (typeof displayValue === 'number') values.push(displayValue);
                    }
                  }
                }
              }
            });
          } else {
            filteredStations.forEach((station) => {
              const observation = getObservation(station, obsKey);
              if (observation) {
                const paramIndex = station.reading_config.findIndex(config => config.name === param);
                if (paramIndex !== -1) {
                  const rawValue = observation.readings[paramIndex];
                  if (rawValue !== null && rawValue !== undefined) {
                    const displayValue = convertForDisplay(param, rawValue);
                    if (typeof displayValue === 'number') values.push(displayValue);
                  }
                }
              }
            });
          }

          // Calculate average (windDirection uses circular mean)
          if (param === 'windDirection') {
            if (values.length > 0) {
              const sinSum = values.reduce((sum, val) => sum + Math.sin((val * Math.PI) / 180), 0);
              const cosSum = values.reduce((sum, val) => sum + Math.cos((val * Math.PI) / 180), 0);
              const avgDirection = (Math.atan2(sinSum, cosSum) * 180) / Math.PI;
              dataPoint[`average_${param}`] = avgDirection < 0 ? avgDirection + 360 : avgDirection;
            } else {
              dataPoint[`average_${param}`] = null;
            }
          } else {
            if (values.length > 0) {
              const sum = values.reduce((a, b) => a + b, 0);
              dataPoint[`average_${param}`] = sum / values.length;
            } else {
              dataPoint[`average_${param}`] = null;
            }
          }
        });
      } else {
        // Show individual station values
        filteredStations.forEach((station) => {
          const observation = getObservation(station, obsKey);
          if (observation) {
            selectedParameters.forEach(param => {
              // Find the index of the parameter in reading_config
              const paramIndex = station.reading_config.findIndex(config => config.name === param);
              if (paramIndex !== -1) {
                const rawValue = observation.readings[paramIndex];
                let displayValue = convertForDisplay(param, rawValue);
                // Treat wind direction as null when wind is calm (speed < 0.5 m/s) - unreliable reading
                if (param === 'windDirection' && typeof displayValue === 'number') {
                  const windSpeedIndex = station.reading_config.findIndex(config => config.name === 'windSpeed');
                  const rawWindSpeed = windSpeedIndex >= 0 ? observation.readings[windSpeedIndex] : null;
                  if (rawWindSpeed === null || rawWindSpeed === undefined || rawWindSpeed < CALM_WIND_THRESHOLD_MS) {
                    displayValue = null;
                  }
                }
                dataPoint[`station_${station.station_reference}_${param}`] = displayValue;
              } else {
                dataPoint[`station_${station.station_reference}_${param}`] = null;
              }
            });
          } else {
            // No observation at this timestamp for this station
            selectedParameters.forEach(param => {
              dataPoint[`station_${station.station_reference}_${param}`] = null;
            });
          }
        });
      }

      return dataPoint;
    });

    // Add per-station wind direction arrow markers at regular intervals
    const hasWindDirection = selectedParameters.includes('windDirection');
    if (hasWindDirection && dataPoints.length > 0) {
      const arrowInterval = Math.max(1, Math.floor(dataPoints.length / 20));

      dataPoints.forEach((point, index) => {
        if (index % arrowInterval !== 0) return;

        if (showAverage) {
          const avgWindDir = point['average_windDirection'];
          if (typeof avgWindDir === 'number') {
            point.windArrowY_average = avgWindDir;
          }
        } else {
          filteredStations.forEach(station => {
            const windDirKey = `station_${station.station_reference}_windDirection`;
            const windDir = point[windDirKey];
            if (typeof windDir === 'number') {
              point[`windArrowY_${station.station_reference}`] = windDir;
            }
          });
        }
      });
    }

    return dataPoints;
  }, [filteredStations, selectedParameters, circuitTimeZone, convertForDisplay, showAverage, dateRange, downsamplingScale]);

  // Calculate parameter ranges and detect if we need separate y-axes
  const parameterRanges = useMemo(() => {
    if (chartData.length === 0 || selectedParameters.length === 0) {
      return [];
    }

    const ranges: Array<{ param: string; min: number; max: number; center: number }> = [];
    
    selectedParameters.forEach(param => {
      const values: number[] = [];
      
      chartData.forEach(point => {
        if (showAverage) {
          const averageKey = `average_${param}`;
          const value = point[averageKey];
          if (typeof value === 'number' && !isNaN(value) && value !== null) {
            values.push(value);
          }
        } else {
          filteredStations.forEach(station => {
            const stationKey = `station_${station.station_reference}_${param}`;
            const value = point[stationKey];
            if (typeof value === 'number' && !isNaN(value) && value !== null) {
              values.push(value);
            }
          });
        }
      });

      if (values.length > 0) {
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);
        ranges.push({ 
          param, 
          min: minValue, 
          max: maxValue, 
          center: (minValue + maxValue) / 2 
        });
      }
    });

    return ranges.sort((a, b) => a.center - b.center);
  }, [chartData, selectedParameters, filteredStations, showAverage]);

  // Determine y-axis groups based on user selection (single or dual)
  const yAxisGroups = useMemo(() => {
    if (parameterRanges.length === 0) {
      return [];
    }

    const overallMin = Math.min(...parameterRanges.map(r => r.min));
    const overallMax = Math.max(...parameterRanges.map(r => r.max));

    const buildGroup = (
      yAxisId: string,
      parameters: string[],
      min: number,
      max: number,
    ): { yAxisId: string; parameters: string[]; domain: [number, number] } => {
      const range = max - min;
      const padding = range > 0 ? range * 0.05 : Math.max(1, Math.abs(min) * 0.1);
      const dataTop = Math.ceil(max + padding);
      const dataBottom = Math.floor(min - padding);

      if (yAxisScale === 'custom') {
        const bottom = yAxisStartValue;
        const top = yAxisEndValue ?? dataTop;
        return { yAxisId, parameters, domain: [bottom, top] };
      }

      return { yAxisId, parameters, domain: [dataBottom, dataTop] };
    };

    // Single y-axis mode (default)
    if (!useDualYAxis || parameterRanges.length === 1) {
      return [buildGroup(yAxisSide, parameterRanges.map(r => r.param), overallMin, overallMax)];
    }

    // Dual y-axis mode: split parameters by selected order (alternating: 0,2,4... → left, 1,3,5... → right)
    const rangeByParam = new Map(parameterRanges.map(r => [r.param, r]));
    const lowerGroup: typeof parameterRanges = [];
    const upperGroup: typeof parameterRanges = [];
    selectedParameters.forEach((param, index) => {
      const range = rangeByParam.get(param);
      if (range) {
        if (index % 2 === 0) {
          lowerGroup.push(range);
        } else {
          upperGroup.push(range);
        }
      }
    });

    // If one side has no params (e.g. only even-indexed params have data), fall back to single axis
    if (lowerGroup.length === 0 || upperGroup.length === 0) {
      return [buildGroup(yAxisSide, parameterRanges.map(r => r.param), overallMin, overallMax)];
    }

    const lowerMin = Math.min(...lowerGroup.map(r => r.min));
    const lowerMax = Math.max(...lowerGroup.map(r => r.max));
    const upperMin = Math.min(...upperGroup.map(r => r.min));
    const upperMax = Math.max(...upperGroup.map(r => r.max));

    return [
      buildGroup('left', lowerGroup.map(r => r.param), lowerMin, lowerMax),
      buildGroup('right', upperGroup.map(r => r.param), upperMin, upperMax),
    ];
  }, [parameterRanges, selectedParameters, useDualYAxis, yAxisSide, yAxisScale, yAxisStartValue, yAxisEndValue]);

  // Numeric x-axis ticks (ms) for time-based scale; interval depends on range
  const xAxisTicks = useMemo(() => {
    if (chartData.length === 0) return undefined;

    const firstMs = chartData[0].timeMs;
    const lastMs = chartData[chartData.length - 1].timeMs;
    const timeRangeMs = lastMs - firstMs;
    const timeRangeHours = timeRangeMs / (1000 * 60 * 60);

    let tickIntervalMinutes: number;
    if (timeRangeHours > 24) {
      tickIntervalMinutes = 60;
    } else if (timeRangeHours > 6) {
      tickIntervalMinutes = 60;
    } else if (timeRangeHours > 2) {
      tickIntervalMinutes = 30;
    } else {
      tickIntervalMinutes = 10;
    }

    const intervalMs = tickIntervalMinutes * 60 * 1000;
    const roundedStart = Math.floor(firstMs / intervalMs) * intervalMs;
    const ticks: number[] = [];
    for (let t = roundedStart; t <= lastMs; t += intervalMs) {
      ticks.push(t);
    }
    return ticks.length > 0 ? ticks : undefined;
  }, [chartData]);

  // Generate chart series for selected parameters and stations
  const dataLabelFontSize = variant === 'compact' ? 8 : 10;
  const chartSeries = useMemo(() => {
    const series: React.ReactElement[] = [];
    
    // Helper to find which yAxisId a parameter should use
    const getYAxisIdForParameter = (param: string): string => {
      const group = yAxisGroups.find(g => g.parameters.includes(param));
      return group?.yAxisId || 'left';
    };
    
    // Calculate color index for each station+parameter combination
    // This ensures consistent colors across the chart
    let combinationColorIndex = 0;
    
    selectedParameters.forEach((param, paramIndex) => {
      const field = getField(param);
      const displayName = field?.display_name || param;
      const yAxisId = getYAxisIdForParameter(param);
      
      // Parameter-based color (used when useCombinationColors is false)
      const paramColor = PARAMETER_COLORS[paramIndex % PARAMETER_COLORS.length];
      
      if (showAverage) {
        // Show average line
        const averageKey = `average_${param}`;
        const seriesName = `${displayName} - Average`;
        // For average, always use parameter color
        const color = paramColor;

        if (chartType === 'line') {
          series.push(
            <Line
              key={averageKey}
              type="monotone"
              dataKey={averageKey}
              name={seriesName}
              stroke={color}
              strokeWidth={3}
              dot={false}
              connectNulls={false}
              yAxisId={yAxisId}
              isAnimationActive={chartAnimationsEnabled}
            >
              {showDataLabels && (
                <LabelList
                  dataKey={averageKey}
                  position="top"
                  fill={color}
                  fontSize={dataLabelFontSize}
                  formatter={(value: unknown) => typeof value === 'number' ? value.toFixed(1) : String(value ?? '')}
                />
              )}
            </Line>
          );
        } else {
          series.push(
            <Bar
              key={averageKey}
              dataKey={averageKey}
              name={seriesName}
              fill={color}
              fillOpacity={1}
              yAxisId={yAxisId}
              isAnimationActive={chartAnimationsEnabled}
              animationDuration={300}
            >
              {showDataLabels && (
                <LabelList
                  dataKey={averageKey}
                  position="top"
                  fill={isDark ? '#fff' : '#000'}
                  fontSize={dataLabelFontSize}
                  formatter={(value: unknown) => typeof value === 'number' ? value.toFixed(1) : String(value ?? '')}
                />
              )}
            </Bar>
          );
        }
      } else {
        // Show individual station lines
        filteredStations.forEach((station, stationIndex) => {
          const stationKey = `station_${station.station_reference}_${param}`;
          const seriesName = `${displayName} - Station ${station.station_reference}`;
          
          let color: string;
          let strokeDasharray: string | undefined;
          let opacity = 1;
          
          if (useCombinationColors) {
            // Mode: Each station+parameter combination gets its own unique color
            color = COMBINATION_COLORS[combinationColorIndex % COMBINATION_COLORS.length];
            combinationColorIndex++;
            // Solid lines, full opacity for combination colors
            strokeDasharray = undefined;
            opacity = 1;
          } else {
            // Mode: Parameter-based colors with dashed lines to differentiate stations
            color = paramColor;
            // Different line styles for different stations within the same parameter
            if (stationIndex === 0) {
              // First station: solid line, full opacity
              strokeDasharray = undefined;
              opacity = 1;
            } else if (stationIndex === 1) {
              // Second station: dashed
              strokeDasharray = '5 5';
              opacity = 0.9;
            } else if (stationIndex === 2) {
              // Third station: longer dashes
              strokeDasharray = '10 5';
              opacity = 0.8;
            } else {
              // Fourth+ station: dotted
              strokeDasharray = '2 2';
              opacity = 0.7;
            }
          }

          if (chartType === 'line') {
            series.push(
              <Line
                key={stationKey}
                type="monotone"
                dataKey={stationKey}
                name={seriesName}
                stroke={color}
                strokeWidth={2}
                strokeDasharray={strokeDasharray}
                opacity={opacity}
                dot={false}
                connectNulls={false}
                yAxisId={yAxisId}
                isAnimationActive={chartAnimationsEnabled}
              >
                {showDataLabels && (
                  <LabelList
                    dataKey={stationKey}
                    position="top"
                    fill={color}
                    fontSize={dataLabelFontSize}
                    formatter={(value: unknown) => typeof value === 'number' ? value.toFixed(1) : String(value ?? '')}
                  />
                )}
              </Line>
            );
          } else {
            series.push(
              <Bar
                key={stationKey}
                dataKey={stationKey}
                name={seriesName}
                fill={color}
                fillOpacity={1}
                opacity={opacity}
                yAxisId={yAxisId}
                isAnimationActive={chartAnimationsEnabled}
                animationDuration={300}
              >
                {showDataLabels && (
                  <LabelList
                    dataKey={stationKey}
                    position="top"
                    fill={isDark ? '#fff' : '#000'}
                    fontSize={dataLabelFontSize}
                    formatter={(value: unknown) => typeof value === 'number' ? value.toFixed(1) : String(value ?? '')}
                  />
                )}
              </Bar>
            );
          }
        });
      }
    });
    
    return series;
  }, [filteredStations, chartType, selectedParameters, getField, showAverage, yAxisGroups, useCombinationColors, chartAnimationsEnabled, showDataLabels, isDark, dataLabelFontSize]);

  // Generate session reference areas for the chart.
  // Use numeric time (ms) for x1/x2 so sessions render reliably with any data density;
  // categorical x1/x2 required data points inside each session and broke with many points.
  const sessionReferenceAreas = useMemo(() => {
    if (sessions.length === 0 || chartData.length === 0) {
      return [];
    }

    const chartStartMs = chartData[0].timeMs;
    const chartEndMs = chartData[chartData.length - 1].timeMs;

    return sessions.map((session, index) => {
      const sessionStartMs = new Date(session.start_time).getTime();
      const sessionEndMs = new Date(session.end_time).getTime();

      // Only render if session overlaps the chart's time range
      if (sessionEndMs < chartStartMs || sessionStartMs > chartEndMs) {
        return null;
      }

      // Clamp to chart domain so the area stays within the visible scale
      const x1 = Math.max(sessionStartMs, chartStartMs);
      const x2 = Math.min(sessionEndMs, chartEndMs);

      return (
        <ReferenceArea
          key={`session-${index}`}
          x1={x1}
          x2={x2}
          yAxisId={yAxisGroups[0]?.yAxisId || 'left'}
          fill={isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}
          fillOpacity={1}
          stroke={isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}
          strokeWidth={1}
          ifOverflow="visible"
        >
          <Label
            value={session.session_name}
            position="insideTop"
            fill={isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'}
            fontSize={variant === 'compact' ? 9 : 11}
            fontWeight={500}
            offset={5}
          />
        </ReferenceArea>
      );
    }).filter(Boolean);
  }, [sessions, chartData, isDark, yAxisGroups, variant]);

  // In compact (map panel) mode, observe chart container resize so chart scales when panel is resized
  useEffect(() => {
    if (variant !== 'compact' || chartData.length === 0) return;

    const container = chartContainerRef.current;
    if (!container) return;

    const updateHeight = () => {
      if (chartContainerRef.current) {
        const rect = chartContainerRef.current.getBoundingClientRect();
        setChartHeight(Math.max(180, rect.height));
      }
    };

    const ro = new ResizeObserver(updateHeight);
    ro.observe(container);
    return () => ro.disconnect();
  }, [variant, chartData.length]);

  const chartColors = {
    grid: isDark ? '#444' : '#ddd',
    text: isDark ? '#fff' : '#000',
  };

  // Reduced font sizes for compact (map panel) variant
  const chartFontSizes = variant === 'compact'
    ? { axis: 9, legend: 10, dataLabel: 8 }
    : { axis: 11, legend: 12, dataLabel: 10 };

  // Renders the chart content for reuse in both normal and fullscreen views
  const renderChartContent = (height: number) => {
    const hasWindDirection = selectedParameters.includes('windDirection');
    const ChartComponent = hasWindDirection ? ComposedChart : (chartType === 'line' ? LineChart : BarChart);
    const isCompact = variant === 'compact';
    const hasLegend = showLegend && selectedParameters.length > 0;
    const hasRightAxis = yAxisGroups.some(g => g.yAxisId === 'right');
    const hasLeftAxis = yAxisGroups.some(g => g.yAxisId === 'left');

    const chartMargins = isCompact
      ? {
          top: 4,
          right: hasRightAxis ? 36 : 4,
          left: hasLeftAxis ? 0 : 0,
          bottom: 2,
        }
      : {
          top: 5,
          right: hasRightAxis ? 80 : 30,
          left: hasLeftAxis ? 20 : 10,
          bottom: hasLegend ? 60 : 10,
        };

    const xAxisHeight = isCompact ? 36 : 50;
    const yAxisWidth = isCompact ? 30 : undefined;

    const chartInner = (
      <ChartComponent
        data={chartData}
        margin={chartMargins}
        {...(chartType === 'bar' && {
          barSize: Math.max(4, Math.min(12, Math.floor(400 / Math.max(1, chartData.length)))),
          barCategoryGap: 2,
        })}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
        {sessionReferenceAreas}
        <XAxis
          dataKey="timeMs"
          type="number"
          domain={[chartData[0]?.timeMs ?? 'dataMin', chartData[chartData.length - 1]?.timeMs ?? 'dataMax']}
          stroke={chartColors.text}
          style={{ fill: chartColors.text }}
          angle={0}
          height={xAxisHeight}
          ticks={xAxisTicks}
          tick={(props) => <CustomXAxisTick {...props} textColor={chartColors.text} timeZone={circuitTimeZone} fontSize={chartFontSizes.axis} />}
        />
        {yAxisGroups.map((group) => {
          const paramLabels = group.parameters.map(param => {
            const field = getField(param);
            return field?.display_name || param;
          });
          return (
            <YAxis
              key={group.yAxisId}
              yAxisId={group.yAxisId}
              orientation={group.yAxisId === 'left' ? 'left' : 'right'}
              stroke={chartColors.text}
              style={{ fill: chartColors.text, fontSize: chartFontSizes.axis }}
              width={yAxisWidth}
              domain={group.domain}
              niceTicks={yAxisScale === 'auto' ? 'snap125' : 'none'}
              allowDataOverflow={yAxisScale === 'custom'}
              label={{
                value: yAxisGroups.length > 1 ? paramLabels.join(', ') : '',
                angle: -90,
                position: 'center',
                dx: group.yAxisId === 'left' ? (isCompact ? -12 : -20) : (isCompact ? 12 : 20),
                style: { fill: chartColors.text, fontSize: Math.max(8, chartFontSizes.axis - (group.parameters.length - 1)), fontWeight: 500 },
              }}
            />
          );
        })}
        <Tooltip
          contentStyle={{
            backgroundColor: isDark ? '#1f1f1f' : '#fff',
            border: `1px solid ${chartColors.grid}`,
            color: chartColors.text,
            fontSize: chartFontSizes.axis,
          }}
          labelStyle={{ color: chartColors.text, fontSize: chartFontSizes.axis }}
          labelFormatter={(label: React.ReactNode) => {
            if (typeof label === 'number' && circuitTimeZone) {
              const d = new Date(label);
              return d.toLocaleString('en-US', {
                timeZone: circuitTimeZone,
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
              });
            }
            return String(label ?? '');
          }}
          formatter={(value, name) => {
            if (!name || String(name).startsWith('windArrowY')) return null;
            if (typeof value === 'number') return [value.toFixed(1), name];
            return [value, name];
          }}
          filterNull={true}
        />
        <Legend wrapperStyle={{ display: 'none' }} />
        {chartSeries}
        {hasWindDirection && (() => {
          const windYAxisId = yAxisGroups.find(g => g.parameters.includes('windDirection'))?.yAxisId || 'left';
          if (showAverage) {
            return (
              <Scatter
                key="windArrow_average"
                dataKey="windArrowY_average"
                yAxisId={windYAxisId}
                shape={<WindArrow convention={windArrowConvention} />}
                legendType="none"
                isAnimationActive={chartAnimationsEnabled}
              />
            );
          }
          return filteredStations.map(station => (
            <Scatter
              key={`windArrow_${station.station_reference}`}
              dataKey={`windArrowY_${station.station_reference}`}
              yAxisId={windYAxisId}
              shape={<WindArrow stationRef={station.station_reference} convention={windArrowConvention} />}
              legendType="none"
              isAnimationActive={chartAnimationsEnabled}
            />
          ));
        })()}
      </ChartComponent>
    );

    const legendElement = hasLegend ? (
      <div style={{
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: isCompact ? '0 0 2px' : undefined,
        ...(isCompact ? {} : {
          position: 'absolute' as const,
          bottom: 0,
          left: 0,
          right: 0,
          minHeight: 60,
        }),
      }}>
        <CustomLegend
          parameters={selectedParameters}
          stations={showAverage ? [] : selectedStations}
          getField={getField}
          textColor={chartColors.text}
          useCombinationColors={useCombinationColors}
          fontSize={chartFontSizes.legend}
          hideSectionLabels={isCompact}
          compact={isCompact}
        />
      </div>
    ) : null;

    if (height <= 0) {
      return null;
    }

    const compactLegendReserve = hasLegend ? 26 : 0;
    const compactPlotHeight = Math.max(60, height - compactLegendReserve);

    if (isCompact) {
      return (
        <div style={{ width: '100%', height, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ height: compactPlotHeight, minHeight: 0, position: 'relative' }}>
            <ResponsiveContainer width="100%" height={compactPlotHeight} debounce={250}>
              {chartInner}
            </ResponsiveContainer>
          </div>
          {legendElement}
        </div>
      );
    }

    return (
      <div style={{ width: '100%', height, position: 'relative' }}>
        <ResponsiveContainer width="100%" height={height} debounce={250}>
          {chartInner}
        </ResponsiveContainer>
        {legendElement}
      </div>
    );
  };

  if (stations.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0', color: chartColors.text }}>
          No station data available
        </div>
      </Card>
    );
  }

  // Label style for controls
  const labelStyle: React.CSSProperties = {
    color: chartColors.text,
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  };

  // Compact layout for map panel settings drawer: vertical stacking, grouped sections
  const controlsContentCompact = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, width: '100%' }}>
      {/* Data selection */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ ...labelStyle, fontSize: 12 }}>Weather Parameters</label>
          <Select
            mode="multiple"
            value={selectedParameters}
            onChange={setSelectedParameters}
            style={{ width: '100%' }}
            placeholder="Select parameters"
            options={fieldOptions}
            maxTagCount="responsive"
            size="small"
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ ...labelStyle, fontSize: 12 }}>Stations</label>
          <Select
            mode="multiple"
            value={selectedStations}
            onChange={setSelectedStations}
            style={{ width: '100%' }}
            placeholder="Select stations"
            options={stations.map(s => ({ value: s.station_reference, label: `Station ${s.station_reference}` }))}
            maxTagCount="responsive"
            size="small"
          />
        </div>
      </div>

      {/* Chart type & display */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Chart Type</label>
        <Radio.Group value={chartType} onChange={(e) => setChartType(e.target.value)} buttonStyle="solid" size="small">
          <Radio.Button value="line">Line</Radio.Button>
          <Radio.Button value="bar">Bar</Radio.Button>
        </Radio.Group>
      </div>

      {/* Display options - compact grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Display</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Average</span>
            <Switch checked={showAverage} onChange={setShowAverage} size="small" checkedChildren="Average" unCheckedChildren="Individual" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Dual Y-Axis</span>
            <Switch checked={useDualYAxis} onChange={setUseDualYAxis} size="small" disabled={selectedParameters.length < 2} checkedChildren="Dual" unCheckedChildren="Single" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Colors</span>
            <Switch checked={useCombinationColors} onChange={setUseCombinationColors} size="small" checkedChildren="Colors" unCheckedChildren="Dashed" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Data Labels</span>
            <Switch checked={showDataLabels} onChange={setShowDataLabels} size="small" checkedChildren="On" unCheckedChildren="Off" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Legend</span>
            <Switch checked={showLegend} onChange={setShowLegend} size="small" checkedChildren="On" unCheckedChildren="Off" />
          </div>
          {selectedParameters.includes('windDirection') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Wind Arrows</span>
              <Switch
                checked={windArrowConvention === 'coming-from'}
                onChange={(checked) => setWindArrowConvention(checked ? 'coming-from' : 'blowing-to')}
                size="small"
                checkedChildren="From"
                unCheckedChildren="To"
              />
            </div>
          )}
          {showDownsamplingSelect && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Data scale</span>
              <Select
                value={downsamplingScale}
                onChange={(v) => setDownsamplingScale(v)}
                size="small"
                style={{ minWidth: 110 }}
                options={DOWNSAMPLING_SCALE_OPTIONS}
                title="Auto: 1-min (&lt;6h), 10-min (6-24h), 30-min (&gt;24h)"
              />
            </div>
          )}
        </div>
      </div>

      {/* Y-Axis options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ ...labelStyle, fontSize: 12 }}>Y-Axis</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Side</span>
            <Radio.Group value={yAxisSide} onChange={(e) => setYAxisSide(e.target.value)} buttonStyle="solid" size="small" disabled={useDualYAxis}>
              <Radio.Button value="left">Left</Radio.Button>
              <Radio.Button value="right">Right</Radio.Button>
            </Radio.Group>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Scale</span>
            <Radio.Group value={yAxisScale} onChange={(e) => setYAxisScale(e.target.value)} buttonStyle="solid" size="small">
              <Radio.Button value="auto">Auto</Radio.Button>
              <Radio.Button value="custom">Custom</Radio.Button>
            </Radio.Group>
            {yAxisScale === 'custom' && (
              <>
                <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>Start at</span>
                <InputNumber value={yAxisStartValue} onChange={(v) => setYAxisStartValue(v ?? 0)} size="small" style={{ width: 72 }} title="Y-axis minimum" />
                <span style={{ ...labelStyle, fontSize: 11, fontWeight: 400 }}>End at</span>
                <InputNumber value={yAxisEndValue ?? undefined} onChange={(v) => setYAxisEndValue(v ?? null)} size="small" style={{ width: 72 }} placeholder="auto" title="Y-axis maximum" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Full layout for Observations page (horizontal, wide)
  const controlsContent = (
        <Space size="large" orientation="vertical" style={{ width: '100%' }}>
          <Space size="large" style={{ width: '100%', alignItems: 'center' }} wrap>
            <div style={{ flex: 1, minWidth: '500px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Weather Parameters:
              </label>
              <Select
                mode="multiple"
                value={selectedParameters}
                onChange={setSelectedParameters}
                style={{ flex: 1, minWidth: 0, maxWidth: 'none', width: '100%' }}
                placeholder="Select weather parameters to plot"
                options={fieldOptions}
                maxTagCount="responsive"
              />
            </div>

            <div style={{ flex: 1, minWidth: '300px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Stations:
              </label>
              <Select
                mode="multiple"
                value={selectedStations}
                onChange={setSelectedStations}
                style={{ flex: 1, minWidth: 0, maxWidth: 'none', width: '100%' }}
                placeholder="Select stations to include"
                options={stations.map(s => ({
                  value: s.station_reference,
                  label: `Station ${s.station_reference}`,
                }))}
                maxTagCount="responsive"
              />
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Chart Type:
              </label>
              <Radio.Group
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                buttonStyle="solid"
              >
                <Radio.Button value="line">Line Chart</Radio.Button>
                <Radio.Button value="bar">Bar Chart</Radio.Button>
              </Radio.Group>
            </div>
          </Space>
          
          <Space size="large" style={{ width: '100%', alignItems: 'center' }} wrap>
            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Show Average:
              </label>
              <Switch
                checked={showAverage}
                onChange={setShowAverage}
                checkedChildren="Average"
                unCheckedChildren="Individual"
              />
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Dual Y-Axis:
              </label>
              <Switch
                checked={useDualYAxis}
                onChange={setUseDualYAxis}
                checkedChildren="Dual"
                unCheckedChildren="Single"
                disabled={selectedParameters.length < 2}
              />
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Y-Axis:
              </label>
              <Radio.Group
                value={yAxisSide}
                onChange={(e) => setYAxisSide(e.target.value)}
                buttonStyle="solid"
                size="small"
                disabled={useDualYAxis}
              >
                <Radio.Button value="left">Left</Radio.Button>
                <Radio.Button value="right">Right</Radio.Button>
              </Radio.Group>
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Y-Axis scale:
              </label>
              <Radio.Group
                value={yAxisScale}
                onChange={(e) => setYAxisScale(e.target.value)}
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="auto">Auto</Radio.Button>
                <Radio.Button value="custom">Custom</Radio.Button>
              </Radio.Group>
              {yAxisScale === 'custom' && (
                <>
                  <span style={{ ...labelStyle, fontSize: 12 }}>Start at</span>
                  <InputNumber
                    value={yAxisStartValue}
                    onChange={(v) => setYAxisStartValue(v ?? 0)}
                    size="small"
                    style={{ width: 80 }}
                    title="Y-axis minimum value"
                  />
                  <span style={{ ...labelStyle, fontSize: 12 }}>End at</span>
                  <InputNumber
                    value={yAxisEndValue ?? undefined}
                    onChange={(v) => setYAxisEndValue(v ?? null)}
                    size="small"
                    style={{ width: 80 }}
                    placeholder="auto"
                    title="Y-axis maximum value"
                  />
                </>
              )}
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Color Mode:
              </label>
              <Switch
                checked={useCombinationColors}
                onChange={setUseCombinationColors}
                checkedChildren="Colors"
                unCheckedChildren="Dashed"
              />
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Data Labels:
              </label>
              <Switch
                checked={showDataLabels}
                onChange={setShowDataLabels}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </div>

            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>
                Legend:
              </label>
              <Switch
                checked={showLegend}
                onChange={setShowLegend}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
            </div>
            {selectedParameters.includes('windDirection') && (
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={labelStyle}>
                  Wind Arrows:
                </label>
                <Switch
                  checked={windArrowConvention === 'coming-from'}
                  onChange={(checked) => setWindArrowConvention(checked ? 'coming-from' : 'blowing-to')}
                  checkedChildren="From"
                  unCheckedChildren="To"
                />
              </div>
            )}
            {showDownsamplingSelect && (
              <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={labelStyle}>
                  Data scale:
                </label>
                <Select
                  value={downsamplingScale}
                  onChange={(v) => setDownsamplingScale(v)}
                  style={{ minWidth: 120 }}
                  options={DOWNSAMPLING_SCALE_OPTIONS}
                  title="Auto: 1-min (&lt;6h), 10-min (6-24h), 30-min (&gt;24h)"
                />
              </div>
            )}
          </Space>
        </Space>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
      {/* Controls - inline for 'full', in Drawer for 'compact' */}
      {variant === 'full' && (
        <Card
          style={{
            marginBottom: 0,
            backgroundColor: isDark ? '#1f1f1f' : '#fff',
            borderRadius: 0,
          }}
        >
          {controlsContent}
        </Card>
      )}

      {/* Chart */}
      {(() => {
        const chartBodyStyle: React.CSSProperties = {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
          ...(embeddedInWidget
            ? { padding: '6px 10px 8px' }
            : variant === 'compact'
              ? { padding: '4px 8px 6px' }
              : {}),
        };

        const chartBody = (
          <>
            {/* Floating settings button for compact variant (overlays chart, no header row) */}
            {variant === 'compact' && !compactActionsRef && (
              <div style={{ position: 'absolute', top: 4, right: 4, zIndex: 5, display: 'flex', gap: 2 }}>
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  onClick={() => setIsSettingsDrawerOpen(true)}
                  title="Chart settings"
                  size="small"
                  style={{ color: chartColors.text, opacity: 0.7 }}
                />
                {selectedParameters.length > 0 && chartData.length > 0 && !hideFullScreen && (
                  <Button
                    type="text"
                    icon={<FullscreenOutlined />}
                    onClick={() => setIsFullScreen(true)}
                    title="Full screen chart"
                    size="small"
                    style={{ color: chartColors.text, opacity: 0.7 }}
                  />
                )}
              </div>
            )}
            {selectedParameters.length === 0 ? (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: chartColors.text,
                padding: embeddedInWidget ? '8px 0' : '40px 0',
              }}>
                Please select at least one weather parameter to display
              </div>
            ) : chartData.length === 0 ? (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: chartColors.text,
                padding: embeddedInWidget ? '8px 0' : '40px 0',
              }}>
                No data available for the selected parameters
              </div>
            ) : (
              <div ref={chartContainerRef} style={{ flex: 1, minHeight: variant === 'compact' ? 0 : 300, position: 'relative' }}>
                {renderChartContent(chartHeight)}
              </div>
            )}
          </>
        );

        if (embeddedInWidget) {
          return (
            <div style={chartBodyStyle}>
              {chartBody}
            </div>
          );
        }

        return (
          <Card
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              ...(variant === 'compact'
                ? {
                    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: 'none',
                    boxShadow: 'none',
                  }
                : {
                    backgroundColor: isDark ? '#1f1f1f' : '#fff',
                    borderRadius: 0,
                  }),
            }}
            styles={
              variant === 'compact'
                ? {
                    body: {
                      ...chartBodyStyle,
                      backgroundColor: 'transparent',
                    },
                    header: {
                      backgroundColor: 'transparent',
                      borderBottomColor: 'transparent',
                    },
                  }
                : { body: { ...chartBodyStyle, padding: '8px' } }
            }
            title={
              !hideTitle ? (
                <span style={{ color: chartColors.text }}>
                  All Stations Comparison
                </span>
              ) : undefined
            }
            extra={
              variant === 'compact' && compactActionsRef ? undefined : (
                variant === 'full' ? (
                  <Space>
                    {selectedParameters.length > 0 && chartData.length > 0 && !hideFullScreen && (
                      <Button
                        type="text"
                        icon={<FullscreenOutlined />}
                        onClick={() => setIsFullScreen(true)}
                        title="Full screen chart"
                        style={{ color: chartColors.text }}
                      />
                    )}
                  </Space>
                ) : undefined
              )
            }
          >
            {chartBody}
          </Card>
        );
      })()}

      {/* Settings drawer for compact variant */}
      {variant === 'compact' && (
        <Drawer
          title="Chart Settings"
          placement="right"
          onClose={() => setIsSettingsDrawerOpen(false)}
          open={isSettingsDrawerOpen}
          size={Math.min(520, typeof window !== 'undefined' ? window.innerWidth * 0.9 : 520)}
          styles={{
            body: { backgroundColor: isDark ? '#1f1f1f' : '#fff' },
            header: { backgroundColor: isDark ? '#1f1f1f' : '#fff', color: chartColors.text },
          }}
        >
          <Card style={{ backgroundColor: 'transparent', border: 'none', boxShadow: 'none' }}>
            {controlsContentCompact}
          </Card>
        </Drawer>
      )}

      {/* Fullscreen chart modal - fits within viewport to avoid scrollbar, resizes with window */}
      <Modal
        open={isFullScreen}
        onCancel={() => setIsFullScreen(false)}
        footer={[
          <Button key="close" onClick={() => setIsFullScreen(false)}>
            Close
          </Button>,
        ]}
        width="100%"
        styles={{
          body: {
            height: 'calc(100vh - 160px)',
            maxHeight: 'calc(100vh - 160px)',
            padding: 16,
            overflow: 'hidden',
            backgroundColor: isDark ? '#1f1f1f' : '#fff',
          },
          wrapper: { maxWidth: '100vw' },
        }}
        style={{ top: 24, paddingBottom: 0 }}
        title="All Stations Comparison - Full Screen"
      >
        {chartData.length > 0 && renderChartContent(fullscreenChartHeight)}
      </Modal>
    </div>
  );
};
